import { useGT } from 'gt-react';
import { Separator } from 'src/components/ui/separator';
import {
  useInventoryPolicyOverrides,
  useRevokeInventoryPolicyOverride,
  type EffectivePolicy,
  type InventoryPolicyOverrideRow,
} from 'src/api/rpc/hooks/procurement/useInventoryPolicyOverrides';
import { OverrideCountBadges } from '../components/OverrideCountBadges';
import {
  OverrideRevokeDialog,
  useOverrideRevoke,
  type OverrideRevokeState,
} from '../components/OverrideRevoke';
import { PolicyOverrideHistory } from './PolicyOverrideHistory';
import { overrideFieldLabel } from './policyOverrideRows';
import { RevokePreviewTable } from './PolicyOverridePreviews';
import {
  PolicyOverrideForm,
  usePolicyOverrideForm,
} from './PolicyOverrideForm';
import { EffectivePolicyValue } from '../../components/EffectivePolicyValue';

// Manual inventory-policy overrides for one product x warehouse: what applies
// right now (base ⊕ the active overlay), every override ever set with its
// status, and a form to add one or edit an active one. Editing revokes the
// current row and creates its replacement (the row chain IS the history), so
// an edited row shows as "replaced" rather than changing in place.
// See docs/procurement/lead-time-overrides.md.

function EffectiveRow({
  label,
  value,
  base,
}: {
  label: string;
  value: number;
  base: number;
}) {
  const overridden = value !== base;
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={overridden ? 'font-semibold text-blue-700' : ''}>
        {value.toLocaleString()}
        {overridden && (
          <span className="ml-1 text-gray-400 line-through">
            {base.toLocaleString()}
          </span>
        )}
      </span>
    </div>
  );
}

function EffectiveBoolRow({
  label,
  value,
  base,
}: {
  label: string;
  value: boolean;
  base: boolean;
}) {
  const gt = useGT();
  const overridden = value !== base;
  const yes = gt('on', { context: 'policy override bool value' });
  const no = gt('off', { context: 'policy override bool value' });
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className={overridden ? 'font-semibold text-blue-700' : ''}>
        {value ? yes : no}
      </span>
    </div>
  );
}

function ForecastRow({
  label,
  value,
}: {
  label: string;
  value: number | null;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span>{value != null ? Math.round(value).toLocaleString() : '—'}</span>
    </div>
  );
}

function EffectiveNullableRow({
  label,
  value,
  base,
}: {
  label: string;
  value: number | null;
  base: number | null;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <EffectivePolicyValue value={value} base={base} />
    </div>
  );
}

function effectiveLevelSourceLabel(
  source: EffectivePolicy['effectiveLevelSource'],
  gt: ReturnType<typeof useGT>,
): string {
  switch (source) {
    case 'forecast_safety_stock_override':
      return gt('Forecast · Safety Stock Override', {
        context: 'effective policy level source',
      });
    case 'forecast_calculated':
      return gt('Forecast · Calculated Safety Stock', {
        context: 'effective policy level source',
      });
    case 'fixed_min':
      return gt('Fixed minimum', { context: 'planning level source' });
    case 'min_override':
      return gt('Min Override', { context: 'effective policy level source' });
    case 'min_calculated':
      return gt('Calculated Min', {
        context: 'effective policy level source',
      });
  }
}

function EffectivePolicySummary({ effective }: { effective: EffectivePolicy }) {
  const gt = useGT();
  return (
    <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
      <EffectiveRow
        label={overrideFieldLabel('minQuantity', gt)}
        value={effective.minQuantity}
        base={effective.baseMinQuantity}
      />
      <EffectiveRow
        label={overrideFieldLabel('maxQuantity', gt)}
        value={effective.maxQuantity}
        base={effective.baseMaxQuantity}
      />
      <EffectiveBoolRow
        label={overrideFieldLabel('autoTransferEligible', gt)}
        value={effective.autoTransferEligible}
        base={effective.baseAutoTransferEligible}
      />
      <EffectiveBoolRow
        label={overrideFieldLabel('autoBuyEligible', gt)}
        value={effective.autoBuyEligible}
        base={effective.baseAutoBuyEligible}
      />
      <EffectiveNullableRow
        label={gt('Safety Stock', {
          context: 'policy override forecast field',
        })}
        value={effective.safetyStock}
        base={effective.baseSafetyStock}
      />
      <EffectiveNullableRow
        label={gt('Reorder Pt', { context: 'policy override forecast field' })}
        value={effective.reorderPoint}
        base={effective.baseReorderPoint}
      />
      <EffectiveNullableRow
        label={gt('Order-up-to', { context: 'policy override forecast field' })}
        value={effective.orderUpToLevel}
        base={effective.baseOrderUpToLevel}
      />
      <ForecastRow
        label={gt('Min Floor', {
          context: 'policy override minimum floor field',
        })}
        value={effective.minFloor}
      />
      <ForecastRow
        label={overrideFieldLabel('orderMultiple', gt)}
        value={effective.orderMultiple}
      />
      <ForecastRow
        label={gt('Effective Protection', {
          context: 'resolved policy level after forecast or min and floor',
        })}
        value={effective.effectiveTriggerLevel}
      />
      <div className="col-span-2 flex items-center justify-between">
        <span className="text-gray-500">
          {gt('Protection Source', {
            context: 'resolved policy level provenance label',
          })}
        </span>
        <span className="font-medium">
          {effectiveLevelSourceLabel(effective.effectiveLevelSource, gt)}
          {effective.minFloorApplied
            ? gt(' · floor applied', {
                context: 'resolved policy level provenance suffix',
              })
            : ''}
        </span>
      </div>
    </div>
  );
}

interface PolicyOverridePanelProps {
  warehouseId: string;
  /** ProductV3 key (the SKU): the policy's product identity. */
  productV3Key?: string | null;
  productVariantId: string | null;
}

// A line with no SKU has no policy to pin, and gating here rather than inside
// the panel keeps the target addressable for everything below. This used to key
// off the variant, which hid the panel for every ProductV3 policy (they all
// have a null variant).
export function PolicyOverridePanel({
  warehouseId,
  productV3Key,
  productVariantId,
}: PolicyOverridePanelProps) {
  const gt = useGT();
  if (!productV3Key) {
    return (
      <p className="text-xs text-gray-400">
        {gt('No policy to override for this line (no product reference).', {
          context: 'policy override panel empty state',
        })}
      </p>
    );
  }
  return (
    <AddressablePolicyPanel
      warehouseId={warehouseId}
      productV3Key={productV3Key}
      productVariantId={productVariantId}
    />
  );
}

// Confirming a revoke: what it means, and what each field reverts to. A row
// whose window has not started is a different question — nothing it sets is
// applying, so there is no revert to preview — which is the only branch here.
function PolicyRevokeDialog({
  revoke,
  overrides,
  effective,
}: {
  revoke: OverrideRevokeState<InventoryPolicyOverrideRow>;
  overrides: readonly InventoryPolicyOverrideRow[];
  effective: EffectivePolicy | null;
}) {
  const gt = useGT();
  const scheduled = revoke.targetScheduled;
  return (
    <OverrideRevokeDialog
      revoke={revoke}
      wide
      title={gt('Revoke override?', {
        context: 'revoke override confirm title',
      })}
      message={
        scheduled
          ? gt(
              'This override has not started yet, so nothing it sets is applying. Revoking cancels it before it does.',
              { context: 'revoke scheduled override confirm message' },
            )
          : gt('Removing this override reverts the fields below.', {
              context: 'revoke override confirm message',
            })
      }
    >
      {revoke.target && !scheduled ? (
        <RevokePreviewTable
          override={revoke.target}
          overrides={overrides}
          effective={effective}
        />
      ) : null}
    </OverrideRevokeDialog>
  );
}

// The SKU is non-null here: PolicyOverridePanel returned early without one.
function AddressablePolicyPanel(target: {
  warehouseId: string;
  productV3Key: string;
  productVariantId: string | null;
}) {
  const gt = useGT();
  const query = useInventoryPolicyOverrides(target);
  const revokeMutation = useRevokeInventoryPolicyOverride();
  const form = usePolicyOverrideForm(target);
  const revoke = useOverrideRevoke<InventoryPolicyOverrideRow>({
    revoke: (o, handlers) =>
      revokeMutation.mutate({ overrideId: o.id }, handlers),
    pending: revokeMutation.isPending,
    successMessage: gt('Override revoked', {
      context: 'policy override toast',
    }),
    // The row being edited just stopped existing; drop the edit with it.
    onRevoked: (o) => {
      if (form.editing?.id === o.id) form.reset();
    },
  });

  const effective = query.data?.effective ?? null;
  const overrides = query.data?.overrides ?? [];
  const needsReviewCount = overrides.filter((o) => o.needsReview).length;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-semibold">
          {gt('Policy overrides', { context: 'policy override panel heading' })}
        </h4>
        <OverrideCountBadges
          activeCount={effective?.activeOverrideCount ?? 0}
          needsReviewCount={needsReviewCount}
        />
      </div>

      {effective ? <EffectivePolicySummary effective={effective} /> : null}

      <PolicyOverrideHistory
        overrides={overrides}
        onEdit={form.startEdit}
        onRequestRevoke={revoke.request}
      />

      <Separator className="my-2" />

      <PolicyOverrideForm form={form} effective={effective} />

      <PolicyRevokeDialog
        revoke={revoke}
        overrides={overrides}
        effective={effective}
      />
    </div>
  );
}
